/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  styled,
  Switch,
  SwitchProps,
  Typography,
} from '@mui/material';

import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import { TUpdateUser, updateUser } from '../../../../store/UserSlice/UserApi';
import { primaryButtonProps } from '..';
import { capitalizeFirstLetter } from '../../../../helper';

export const EditModeSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#1E293B',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const convertNotificationObject = (object1) => {
  const object2 = {};

  for (const channel in object1) {
    if (Object.prototype.hasOwnProperty.call(object1, channel)) {
      object2[channel] = {};

      for (const notificationType in object1[channel]) {
        if (
          Object.prototype.hasOwnProperty.call(
            object1[channel],
            notificationType
          )
        ) {
          object2[channel][notificationType] = Object.values(
            object1[channel][notificationType]
          ).some((value) => value === true);
        }
      }
    }
  }

  return object2;
};

const getCheckBoxLabel = (type: string): string => {
  switch (type) {
    case 'toUser':
      return 'To Student';
    case 'toParent':
      return 'To Parent';
    case 'toBilling':
      return 'To Billing';
    default:
      return capitalizeFirstLetter(type);
  }
};

type ICheckValues = {
  email: {
    classReminders: {
      [key: string]: boolean;
    };
    homeworks: {
      [key: string]: boolean;
    };
    payments: {
      [key: string]: boolean;
    };
  };
  whatsapp: {
    classReminders: {
      [key: string]: boolean;
    };
    homeworks: {
      [key: string]: boolean;
    };
    payments: {
      [key: string]: boolean;
    };
  };
};

const initialCheckValues: ICheckValues = {
  email: {
    homeworks: {
      toUser: true,
      toParent: false,
    },
    classReminders: {
      toUser: true,
      toParent: false,
    },
    payments: {
      toUser: true,
      toParent: false,
      toBilling: false,
    },
  },
  whatsapp: {
    homeworks: {
      toUser: true,
      toParent: false,
    },
    classReminders: {
      toUser: true,
      toParent: false,
    },
    payments: {
      toUser: true,
      toParent: false,
      toBilling: false,
    },
  },
};

type ISwitchValues = {
  email: {
    classReminders: boolean;
    homeworks: boolean;
    payments: boolean;
  };
  whatsapp: {
    classReminders: boolean;
    homeworks: boolean;
    payments: boolean;
  };
};

const initialSwitchValues: ISwitchValues = {
  email: {
    homeworks: false,
    classReminders: false,
    payments: false,
  },
  whatsapp: {
    homeworks: false,
    classReminders: false,
    payments: false,
  },
};

const NotificationSettings: React.FC = () => {
  const dispatch = useDispatch();

  // State
  const [switchValues, setSwitchValues] = useState(initialSwitchValues);
  const [checkboxValues, setCheckboxValues] = useState(initialCheckValues);
  const [isLoading, setLoading] = useState(false);

  // Store
  const { user: authUser } = useAppSelector(getAuthState);

  const roleNotifications = authUser?.role?.notifications ?? [];

  useEffect(() => {
    if (authUser) {
      if (authUser.notifications) {
        setCheckboxValues(JSON.parse(JSON.stringify(authUser.notifications)));
        const convertSwitchValues = convertNotificationObject(
          authUser.notifications
        ) as ISwitchValues;
        setSwitchValues(convertSwitchValues);
      } else if (roleNotifications) {
        setCheckboxValues(JSON.parse(JSON.stringify(roleNotifications)));
        const convertSwitchValues = convertNotificationObject(
          roleNotifications
        ) as ISwitchValues;
        setSwitchValues(convertSwitchValues);
      }
    }
  }, [authUser]);

  const getKeysByObjectName = (obj, objectName: string) => {
    const result: string[] = [];

    for (const key in obj) {
      if (obj[key][objectName]) {
        result.push(key);
      }
    }

    return result;
  };

  const homeworksNotifications = getKeysByObjectName(
    roleNotifications,
    'homeworks'
  );

  const classNotifications = getKeysByObjectName(
    roleNotifications,
    'classReminders'
  );

  const paymentNotifications = getKeysByObjectName(
    roleNotifications,
    'payments'
  );

  const handleSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const { name, checked } = event.target;

    setSwitchValues((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        [type]: checked,
      },
    }));

    if (!checked) {
      setCheckboxValues((prevState) => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          [type]: {
            toUser: false,
            toParent: false,
          },
        },
      }));
    }
  };

  const handleCheckBoxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: boolean,
    name: string,
    type: string
  ) => {
    setCheckboxValues((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        [type]: {
          ...prevState[name]?.[type],
          [event.target.name]: value,
        },
      },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    const apiData: TUpdateUser = {
      notifications: checkboxValues,
    };
    await dispatch(updateUser(apiData));

    setLoading(false);
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="24px"
      >
        <Typography variant="h6" fontSize="18px">
          Notification Settings
        </Typography>
        <LoadingButton
          {...primaryButtonProps}
          onClick={handleSubmit}
          loading={isLoading}
        >
          Save
        </LoadingButton>
      </Stack>

      {homeworksNotifications.length ? (
        <Stack marginBottom="24px">
          <Typography variant="subtitle1" fontWeight="600" marginBottom="12px">
            Homework/Topic Request Notification:
          </Typography>

          <Typography variant="subtitle2" maxWidth="336px" marginBottom="20px">
            Set preferences for receiving notifications about homework
            assignments or topic requests.
          </Typography>

          {homeworksNotifications.map((item, index) => (
            <Box key={index}>
              <Grid container spacing={2} marginBottom="10px">
                <Grid item xs={12} sm={6} md={4} display="flex">
                  <FormControlLabel
                    label={capitalizeFirstLetter(item)}
                    labelPlacement="end"
                    sx={{ color: '#1E293B', textAlign: 'right' }}
                    control={
                      <EditModeSwitch
                        name={item}
                        checked={switchValues[item].homeworks}
                        onChange={(event) =>
                          handleSwitchChange(event, 'homeworks')
                        }
                        sx={{ ml: '10px', mr: '20px' }}
                      />
                    }
                  />
                </Grid>
                {roleNotifications &&
                roleNotifications[item].homeworks.types.length
                  ? roleNotifications[item].homeworks.types.map(
                      (typeItem, typeIndex) => (
                        <Grid key={typeIndex} item xs={6} sm={6} md={4}>
                          <FormControlLabel
                            disabled={!switchValues[item].homeworks}
                            label={getCheckBoxLabel(typeItem)}
                            control={
                              <Checkbox
                                name={typeItem}
                                checked={
                                  checkboxValues[item].homeworks[typeItem]
                                }
                                onChange={(event, value) =>
                                  handleCheckBoxChange(
                                    event,
                                    value,
                                    item,
                                    'homeworks'
                                  )
                                }
                              />
                            }
                          />
                        </Grid>
                      )
                    )
                  : null}
              </Grid>
              <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            </Box>
          ))}
        </Stack>
      ) : null}

      <Stack marginBottom="24px">
        <Typography variant="subtitle1" fontWeight="600" marginBottom="12px">
          Class notification
        </Typography>

        <Typography variant="subtitle2" maxWidth="336px" marginBottom="20px">
          Manage how you receive notifications for upcoming classes.
        </Typography>

        {classNotifications.length
          ? classNotifications.map((item, index) => (
              <Box key={index}>
                <Grid container spacing={2} marginBottom="10px">
                  <Grid item xs={12} sm={6} md={4} display="flex">
                    <FormControlLabel
                      label={capitalizeFirstLetter(item)}
                      labelPlacement="end"
                      sx={{ color: '#1E293B', textAlign: 'right' }}
                      control={
                        <EditModeSwitch
                          name={item}
                          checked={switchValues[item].classReminders}
                          onChange={(event) =>
                            handleSwitchChange(event, 'classReminders')
                          }
                          sx={{ ml: '10px', mr: '20px' }}
                        />
                      }
                    />
                  </Grid>
                  {roleNotifications &&
                  roleNotifications[item].classReminders.types.length
                    ? roleNotifications[item].classReminders.types.map(
                        (typeItem, typeIndex) => (
                          <Grid key={typeIndex} item xs={6} sm={6} md={4}>
                            <FormControlLabel
                              disabled={!switchValues[item].classReminders}
                              label={getCheckBoxLabel(typeItem)}
                              control={
                                <Checkbox
                                  name={typeItem}
                                  checked={
                                    checkboxValues[item].classReminders[
                                      typeItem
                                    ]
                                  }
                                  onChange={(event, value) =>
                                    handleCheckBoxChange(
                                      event,
                                      value,
                                      item,
                                      'classReminders'
                                    )
                                  }
                                />
                              }
                            />
                          </Grid>
                        )
                      )
                    : null}
                </Grid>
                <Divider flexItem sx={{ borderStyle: 'dashed' }} />
              </Box>
            ))
          : null}
      </Stack>

      {paymentNotifications.length ? (
        <Stack marginBottom="24px">
          <Typography variant="subtitle1" fontWeight="600" marginBottom="12px">
            Payments notification
          </Typography>

          <Typography variant="subtitle2" maxWidth="336px" marginBottom="20px">
            Manage payments related notifications.
          </Typography>

          {paymentNotifications.map((item, index) => (
            <Box key={index}>
              <Grid container spacing={2} marginBottom="10px">
                <Grid item xs={12} sm={6} md={3} display="flex">
                  <FormControlLabel
                    labelPlacement="end"
                    label={capitalizeFirstLetter(item)}
                    sx={{ color: '#1E293B', textAlign: 'right' }}
                    control={
                      <EditModeSwitch
                        name={item}
                        checked={switchValues[item]?.payments || false}
                        sx={{ ml: '10px', mr: '20px' }}
                        onChange={(event) =>
                          handleSwitchChange(event, 'payments')
                        }
                      />
                    }
                  />
                </Grid>
                {roleNotifications &&
                roleNotifications[item].payments.types.length
                  ? roleNotifications[item].payments.types.map(
                      (typeItem, typeIndex) => (
                        <Grid key={typeIndex} item xs={6} sm={6} md={3}>
                          <FormControlLabel
                            disabled={!switchValues[item]?.payments}
                            label={getCheckBoxLabel(typeItem)}
                            control={
                              <Checkbox
                                name={typeItem}
                                checked={
                                  checkboxValues[item]?.payments?.[typeItem] ||
                                  false
                                }
                                onChange={(event, value) =>
                                  handleCheckBoxChange(
                                    event,
                                    value,
                                    item,
                                    'payments'
                                  )
                                }
                              />
                            }
                          />
                        </Grid>
                      )
                    )
                  : null}
              </Grid>
              <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
};

export default NotificationSettings;
